import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, Loader2, ChevronRight, PartyPopper, Check } from 'lucide-react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { formatINR } from './command-center/constants';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All-time' },
];

/**
 * "Biggest closures" widget. Surfaces the top 5 joined candidates by offered
 * CTC in the selected period. Visible to all internal roles (revenue data,
 * row-scoped via RBAC).
 *
 * Auto-hides when there are no closures yet for the period so the dashboard
 * doesn't show an empty state by default — admins on a fresh org should see
 * onboarding tiles instead.
 */
const TopClosuresWidget = () => {
  const [period, setPeriod] = useState('month');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tracks closures cheered in this session so the button flips to "Cheered"
  // immediately, without waiting for a refetch. Backend also persists
  // `cheered_at` so it survives reloads.
  const [cheeringId, setCheeringId] = useState(null);
  const [cheeredIds, setCheeredIds] = useState(() => new Set());

  const cheer = async (it) => {
    if (cheeringId) return;
    setCheeringId(it.id);
    try {
      await axios.post(
        `${API}/dashboard/cheer-closure/${it.id}`,
        { note: '' },
      );
      setCheeredIds((prev) => new Set(prev).add(it.id));
      toast.success(`Cheered ${it.candidate_name} on Slack`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not post to Slack';
      toast.error(msg);
    } finally {
      setCheeringId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/dashboard/top-closures`, { params: { period, limit: 5 } })
      .then((res) => {
        if (!cancelled) setItems(res.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [period]);

  // Hide entirely when no data — don't show "No closures yet" empty state
  // on the dashboard. Reduces clutter for orgs that don't have closures yet
  // and for narrower time periods where the list is genuinely empty.
  if (!loading && items.length === 0) return null;

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || '';

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="top-closures-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
          <Trophy className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Biggest closures</h3>
          <p className="text-[11px] text-slate-500">{periodLabel} · by offered CTC</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger
            className="h-8 w-[140px] text-xs"
            data-testid="top-closures-period"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value} className="text-xs">
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : (
        <ol className="space-y-1.5">
          {items.map((it, idx) => {
            const isCheered = cheeredIds.has(it.id) || Boolean(it.cheered_at);
            const isCheering = cheeringId === it.id;
            return (
              <li
                key={it.id}
                className="flex items-center gap-2 group"
                data-testid={`top-closure-row-${idx}`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    idx === 0
                      ? 'bg-amber-200 text-amber-900'
                      : idx === 1
                      ? 'bg-slate-200 text-slate-700'
                      : idx === 2
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-slate-50 text-slate-500'
                  }`}
                >
                  {idx + 1}
                </span>
                <Link
                  to={`/candidates?view=${it.candidate_id}`}
                  className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate hover:text-emerald-700 transition"
                  data-testid={`top-closure-name-${idx}`}
                >
                  {it.candidate_name}
                </Link>
                <span className="text-[11px] text-slate-500 truncate hidden sm:inline max-w-[140px]" title={`${it.job_title} · ${it.client_name}`}>
                  {it.job_title} · {it.client_name}
                </span>
                <span
                  className="font-mono font-semibold text-emerald-700 text-sm whitespace-nowrap"
                  data-testid={`top-closure-ctc-${idx}`}
                >
                  ₹{formatINR(it.offered_ctc) || '0'}
                </span>
                <button
                  type="button"
                  disabled={isCheering || isCheered}
                  onClick={() => cheer(it)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition whitespace-nowrap ${
                    isCheered
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                      : 'bg-white border border-amber-300 text-amber-800 hover:bg-amber-50'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  title={isCheered ? 'Already cheered on Slack' : 'Post a Slack celebration for this closure'}
                  data-testid={`top-closure-cheer-${idx}`}
                >
                  {isCheering ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : isCheered ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <PartyPopper className="w-3 h-3" />
                  )}
                  {isCheered ? 'Cheered' : 'Cheer'}
                </button>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 transition flex-shrink-0" />
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default TopClosuresWidget;
